import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useEffect, useRef, useState } from "react";

const MapComponent = ({ position, deviceName, size, apiKey, interactive }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markerRef = useRef(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [hovered, setHovered] = useState(false);

    const createCombinedMarker = (course = 0) => {
        const element = document.createElement('div');
        element.className = 'custom-marker';

        element.innerHTML = `
            <div class="marker-container">
                <!-- Background circle with shadow -->
                <div class="marker-background">
                    <svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <filter id="shadow-${Date.now()}">
                                <feDropShadow dx="0" dy="0.1" stdDeviation="0.3" flood-color="grey"/>
                            </filter>
                        </defs>
                        <circle cx="5" cy="5" r="3.2" style="fill:#163b61; filter:url(#shadow-${Date.now()});"/>
                    </svg>
                </div>

                <!-- Location icon (pin) -->
                <div class="marker-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12,2C8.134,2,5,5.134,5,9c0,5,7,13,7,13s7-8,7-13C19,5.134,15.866,2,12,2z M12,11.5c-1.381,0-2.5-1.119-2.5-2.5 c0-1.381,1.119-2.5,2.5-2.5s2.5,1.119,2.5,2.5C14.5,10.381,13.381,11.5,12,11.5z" fill="green"/>
                    </svg>
                </div>

                <!-- Direction arrow (rotates based on course) -->
                <div class="direction-arrow" style="transform: rotate(${course}deg);">
                    <svg width="560" height="560" viewBox="0 0 11.667 11.667" xmlns="http://www.w3.org/2000/svg">
                        <path d="m4.6303-3.6193v1.2838a4.3079 4.3079 0 0 1 0.57314-0.71067 4.3079 4.3079 0 0 1 0.7101-0.57314h-1.2832z" fill="#283593" transform="rotate(45)"/>
                    </svg>
                </div>
            </div>
        `;

        return addMarkerStyles(element);
    };

    const addMarkerStyles = (element) => {
        const style = document.createElement('style');
        style.textContent = `
            .custom-marker {
                cursor: pointer;
                transition: all 0.3s ease;
                width: 48px;
                height: 48px;
                position: relative;
            }
            .marker-container {
                width: 100%;
                height: 100%;
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .marker-background {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 1;
            }
            .marker-background svg {
                width: 100%;
                height: 100%;
            }
            .marker-icon {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 2;
                width: 24px;
                height: 24px;
            }
            .marker-icon svg {
                width: 100%;
                height: 100%;
            }
            .direction-arrow {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 3;
                transform-origin: center;
                transition: transform 0.5s ease;
            }
            .direction-arrow svg {
                width: 100%;
                height: 100%;
            }
            .custom-marker:hover {
                transform: scale(1.1);
            }
        `;
        element.appendChild(style);
        return element;
    };

    useEffect(() => {
        if (!map.current && position && mapContainer.current && apiKey) {
            map.current = new maplibregl.Map({
                container: mapContainer.current,
                style: {
                    version: 8,
                    sources: {
                        'google-maps': {
                            type: 'raster',
                            tiles: [
                                `https://maps.googleapis.com/maps/vt?pb=!1m5!1m4!1i{z}!2i{x}!3i{y}!4i256!2m3!1e0!2sm!3i671013083!3m12!2sen!3sUS!5e18!12m1!1e47!12m3!1e37!2m1!1ssmartmaps!4e0!5m1!1e0&key=${apiKey}`
                            ],
                            tileSize: 256,
                            attribution: '© Google Maps'
                        }
                    },
                    layers: [
                        {
                            id: 'google-maps',
                            type: 'raster',
                            source: 'google-maps',
                            minzoom: 0,
                            maxzoom: 22
                        }
                    ]
                },
                center: [position.longitude, position.latitude],
                zoom: 16,
            });

            const markerElement = createCombinedMarker(position.course || 0);

            const marker = new maplibregl.Marker({
                element: markerElement,
                anchor: 'center'
            })
                .setLngLat([position.longitude, position.latitude])
                .addTo(map.current);

            markerRef.current = marker;

            const popup = new maplibregl.Popup({
                offset: 25,
                closeButton: true,
                closeOnClick: false
            }).setHTML(
                `<strong>${deviceName || "Device"}</strong><br/>
                 Lat: ${position.latitude.toFixed(4)}, Lng: ${position.longitude.toFixed(4)}<br/>
                 ${position.course !== undefined ? `Course: ${position.course}°` : ''}`
            );

            marker.getElement().addEventListener("click", () => {
                if (popupVisible) {
                    popup.remove();
                    setPopupVisible(false);
                } else {
                    popup.setLngLat([position.longitude, position.latitude]).addTo(map.current);
                    setPopupVisible(true);
                }
            });
        }

        return () => {
            // CLEANUP: remove marker and map
            if (markerRef.current) {
                markerRef.current.remove();
                markerRef.current = null;
            }

            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [apiKey]);

    useEffect(() => {
        if (map.current && position) {
            map.current.setCenter([position.longitude, position.latitude]);

            if (markerRef.current) {
                // Update marker position
                markerRef.current.setLngLat([position.longitude, position.latitude]);

                // Update marker rotation if course is provided
                if (position.course !== undefined) {
                    const markerElement = markerRef.current.getElement();
                    const arrowElement = markerElement.querySelector('.direction-arrow');
                    if (arrowElement) {
                        arrowElement.style.transform = `rotate(${position.course}deg)`;
                    }
                }
            }

            map.current.resize();
        }
    }, [position]);

    return (
        <div
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div
                ref={mapContainer}
                className="map-container"
                style={{
                    minWidth: size,
                    width: '100%',
                    height: size,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    pointerEvents: interactive ? 'auto' : 'none',

                }}

            >
                {!interactive && hovered && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(255,255,255,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            color: '#333',
                            pointerEvents: 'none'
                        }}
                    >
                        Click para interactuar
                    </div>
                )}
            </div>
        </div>

    );
};

export default MapComponent;